import React, { useEffect, useRef, useState } from 'react';
import { T } from './shared/constants';

// A scratch pad beside the question, like the working space on a paper test: draw with a finger, stylus or mouse.
// Each question keeps its own working for the session (going back to a question shows what was written there).
// Strokes are kept as points, not pixels, so the drawing survives the panel being resized or rotated.

const store = new Map(); // question id -> strokes [{ erase, pts: [[x, y], ...] }], cleared when the page reloads

export default function ScratchPad({ questionId, onClose }) {
  const canvasRef = useRef(null), boxRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const strokes = useRef(store.get(questionId) || []);
  const drawing = useRef(null);

  const redraw = () => {
    const c = canvasRef.current, box = boxRef.current; if (!c || !box) return;
    const dpr = window.devicePixelRatio || 1, w = box.clientWidth, h = box.clientHeight;
    if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); c.style.width = w + 'px'; c.style.height = h + 'px'; }
    const g = c.getContext('2d'); g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, w, h);
    g.lineCap = 'round'; g.lineJoin = 'round';
    for (const s of strokes.current) {
      g.globalCompositeOperation = s.erase ? 'destination-out' : 'source-over';
      g.strokeStyle = '#1F2A44'; g.lineWidth = s.erase ? 22 : 2.5;
      g.beginPath(); s.pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
      if (s.pts.length === 1) g.lineTo(s.pts[0][0] + 0.1, s.pts[0][1]);
      g.stroke();
    }
    g.globalCompositeOperation = 'source-over';
  };

  // a new question brings its own working
  useEffect(() => { strokes.current = store.get(questionId) || []; redraw(); }, [questionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ro = new ResizeObserver(redraw); if (boxRef.current) ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const at = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); canvasRef.current.setPointerCapture?.(e.pointerId); drawing.current = { erase: tool === 'eraser', pts: [at(e)] }; strokes.current = [...strokes.current, drawing.current]; redraw(); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); drawing.current.pts.push(at(e)); redraw(); };
  const up = () => { if (!drawing.current) return; drawing.current = null; store.set(questionId, strokes.current); };
  const clear = () => { strokes.current = []; store.set(questionId, []); redraw(); };

  const btn = (on) => ({ padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${on ? T.accent : T.border}`, background: on ? T.accentLo : T.surface, color: T.text, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" });
  return (
    <div className="lumio-scratch" role="dialog" aria-label="Scratch pad" style={{ display: 'flex', flexDirection: 'column', background: T.surface, borderLeft: `1px solid ${T.border}`, borderTop: `1px solid ${T.border}`, boxShadow: '0 -6px 24px rgba(20,20,40,.10)', zIndex: 40 }}>
      <style>{`
        .lumio-scratch{position:fixed;left:0;right:0;bottom:0;height:46vh}
        @media (min-width:900px){.lumio-scratch{left:auto;top:64px;width:min(420px,34vw);height:auto}}
        .lumi-hovering-root{display:none !important} /* the floating Lumi orb would sit on the drawing area */
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: `1px solid ${T.border}` }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: T.text, flex: 1 }}>✏️ Scratch pad</span>
        <button onClick={() => setTool('pen')} aria-pressed={tool === 'pen'} style={btn(tool === 'pen')}>Pen</button>
        <button onClick={() => setTool('eraser')} aria-pressed={tool === 'eraser'} style={btn(tool === 'eraser')}>Eraser</button>
        <button onClick={clear} style={btn(false)}>Clear</button>
        <button onClick={onClose} aria-label="Close scratch pad" style={{ ...btn(false), padding: '7px 10px' }}>✕</button>
      </div>
      <div ref={boxRef} style={{ flex: 1, position: 'relative', background: 'repeating-linear-gradient(#FFFFFF 0 27px, #E8ECF4 27px 28px)', touchAction: 'none' }}>
        <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
          style={{ position: 'absolute', inset: 0, cursor: tool === 'eraser' ? 'cell' : 'crosshair', touchAction: 'none' }} />
      </div>
    </div>
  );
}
